import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from "react-intl";
import bytes from "bytes";
import FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { useDropzone } from "react-dropzone";
import { useModal } from "@/hook";
import Button from "@/component/Button";
import { errorToast, successToast } from "@/component/Toast";
import FileDropIcon from '@/assets/svgs/drop.svg';
import { DownloadIcon, CloseIcon } from "@/assets/svgs/icons";

import { Modal } from ".";


type SampleFileColumn = {
    attribute: string;
    description: string;
}

interface UploadModalProps {
    id: string;
    label: string;
    cleanImportText?: string;
    sampleFileColumns?: SampleFileColumn[];
    sampleFileName?: string;
    upload: (file: File, isCleanImport: boolean) => Promise<any>;
}

const UploadModal: React.FC<UploadModalProps> = (props) => {
    const { id, label, cleanImportText, sampleFileColumns, sampleFileName, upload } = props;
    const intl = useIntl();
    const modal = useModal();

    const cleanImportRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const onClickCancel = () => {
        if (isUploading) {
            return;
        }
        setFile(null);
        modal.close(id);
    };

    const onClickConfirm = () => {
        if (isUploading || !file) {
            return;
        }
        setIsUploading(true);

        upload(file, cleanImportRef.current?.checked ?? false)
            .then(() => {
                successToast(intl.formatMessage({ id: "i00022" }));
                setFile(null);
                setIsUploading(false);
                modal.close(id);
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "i00023" }));
                setIsUploading(false);
            });
    };

    return (
        <Modal id={id}
               title={
                    <UploadModalTitle label={label}
                                      sampleFileName={sampleFileName}
                                      sampleFileColumns={sampleFileColumns}
                    />
                }
               onClose={() => setFile(null)}
        >
            <div className="w-[780px] mt-6 flex flex-col gap-2">
                <FileUploadZone file={file}
                                onChangeFile={setFile}
                />

                <label className="w-fit flex items-center gap-1 cursor-pointer font-[400] text-[14px] leading-[1.5] text-black-500">
                    <input ref={cleanImportRef}
                           type="checkbox"
                           className="input-checkbox-chat42"
                    />
                    {cleanImportText ?? intl.formatMessage({ id: "i00018" })}
                </label>

                <div className="modal-btn-group">
                    <Button variant="secondary"
                            size="sm"
                            disabled={isUploading}
                            onClick={onClickCancel}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button variant="primary"
                            size="sm"
                            disabled={isUploading}
                            onClick={onClickConfirm}
                    >
                        {intl.formatMessage({ id: "b0001" })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

interface UploadModalTitleProps {
    label: string,
    sampleFileName?: string,
    sampleFileColumns?: SampleFileColumn[]
}

export const UploadModalTitle: React.FC<UploadModalTitleProps> = (props) => {
    const { label, sampleFileName: fileName, sampleFileColumns: columns } = props;
    const intl = useIntl();

    const getExcelColumnWidth = (text: string) => (
        Array.from(text).reduce((width, ch) => {
            if (/[가-힣ㄱ-ㅎㅏ-ㅣ一-龥]/.test(ch)) {
                return width + 1.5;
            } else {
                return width + 1;
            }
        }, 0)
    );

    const download = () => {
        if (!fileName || !columns) {
            return;
        }

        const workSheet = XLSX.utils.json_to_sheet([
            Object.fromEntries(columns.map(column => [column.attribute, column.description]))
        ]);
        const widths = columns.map(column => ({ wch: getExcelColumnWidth(column.description) }));
        const heights = Array(XLSX.utils.sheet_to_json(workSheet, { header: 1 }).length).fill({ hpt: 16 });
        const sheetData = {
            ...workSheet,
            "!cols": widths,
            "!rows": heights
        };
        const excelBlob = new Blob(
            [XLSX.write(
                { Sheets: { data: sheetData }, SheetNames: ["data"] },
                { bookType: "xlsx", type: "array" }
            )],
            { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" }
        );

        FileSaver.saveAs(excelBlob, `${fileName}.xlsx`);
    };

    return (
        <div className="flex gap-2">
            <div className="font-[600] text-[16px] leading-[1.5] text-black-500">
                {label}
            </div>
            {fileName && columns && (
                <Button className="gap-x-[3px] text-chat42 font-[400] text-[12px] leading-[1.5]"
                        onClick={() => download()}
                >
                    <DownloadIcon className="size-[13px]" />
                    {intl.formatMessage({ id: "i00015" })}
                </Button>
            )}
        </div>
    );
};


const ACCEPT_FILE_TYPES = {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.ms-excel": [".xlx"]
};
const MAX_FILE_SIZE = 20971520; // 20MB

interface FileUploadZoneProps {
    file: File | null
    onChangeFile: (file: File | null) => void
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = (props) => {
    const { file, onChangeFile } = props;

    const intl = useIntl();

    const onDrop = useCallback((acceptedFiles: any, rejectedFiles: any) => {
        if (acceptedFiles.length > 0) {
            onChangeFile(acceptedFiles[0]);
        }

        if (rejectedFiles.length > 0) {
            if (!(Object.keys(ACCEPT_FILE_TYPES).includes(rejectedFiles[0].type))) {
                errorToast(intl.formatMessage({ id: "i00019" }));
            } else if (rejectedFiles[0].size > MAX_FILE_SIZE) {
                errorToast(intl.formatMessage({ id: "i00020" }));
            } else {
                errorToast(intl.formatMessage({ id: "i00021" }));
            }
        }
    }, [intl, onChangeFile]);

    const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: ACCEPT_FILE_TYPES, maxSize: MAX_FILE_SIZE });

    if (!file) {
        return (
            <div className="w-full">
                <div {...getRootProps()}>
                    <input {...getInputProps({ accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })} />
                    <div className="w-full p-14 border border-black-300 border-dashed bg-black-100 cursor-pointer">
                        <form method="post">
                            <div className="flex justify-center items-center gap-10">
                                <img src={FileDropIcon}
                                    alt="dropdown"
                                    className="size-[52px]"
                                />

                                <div className="flex flex-col gap-4">
                                    <div className="font-[300] text-[16px] text-black-500 leading-[1.5]">
                                        <span>
                                            {intl.formatMessage({ id: "i00016_1" })}
                                        </span>
                                        <span className="text-chat42 underline">
                                            {intl.formatMessage({ id: "i00016_2" })}
                                        </span>
                                        <span>
                                            {intl.formatMessage({ id: "i00016_3" })}
                                        </span>
                                    </div>
                                    <div className="whitespace-pre-wrap font-[300] text-[12px] text-black-400 leading-[1.4]">
                                        {intl.formatMessage({ id: "i00017" })}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full">
            <div className="w-full p-4 flex justify-between items-center gap-4 border border-black-200 rounded-[4px]">
                <div className="flex items-center gap-4">
                    <div className="size-12 flex justify-center items-center bg-chat42-5 border border-black-200 rounded-[6px]">
                        <div className="font-[400] text-[13px] leading-[1.5] text-black-500">
                            {`.${file.name.split(".").slice(-1)[0].toUpperCase()}`}
                        </div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <div className="font-[500] text-[14px] leading-[1.5] text-black-500">
                            {file.name}
                        </div>
                        <div className="font-[400] text-[12px] leading-[1.5] text-black-400">
                            {bytes(file.size)}
                        </div>
                    </div>
                </div>
                <Button onClick={() => onChangeFile(null)}>
                    <CloseIcon className="size-4" />
                </Button>
            </div>
        </div>
    );
};

export default UploadModal;
